<script setup lang="ts">
import {useRouterSectionParams} from '@/ts/composables/stateful/use-route-params';
import {LayoutGoalDisplayType} from '@/ts/types/dto/interventions-dto';
import {watch} from 'vue';
import {useLoadingDataRefNull} from '@/ts/composables/pure/use-loading-data-ref';
import LoadingError from '@/vue/templates/loading-error.vue';
import {useApi} from '@/ts/composables/stateful/use-api';
import LazyImage from '@/vue/atoms/lazy-image.vue';
import GoalShort from '@/vue/molecules/goal-short.vue';
import {useDragToScroll} from '@/ts/composables/pure/use-drag-to-scroll';

const emit = defineEmits<{
  ready: [],
}>();

const {iid, sid} = useRouterSectionParams();

const api = useApi();
const [session, loading, error] = useLoadingDataRefNull(async() => api.me.getSession(iid.value, sid.value));

const {scrollAreaRef, onMouseDown} = useDragToScroll();

watch(loading, (loadingVal) => {
  if (!loadingVal) {
    emit('ready');
  }
});

</script>
<template>
  <loading-error id="session-introduction" :loading="loading" :error="error" type="component">
    <div class="session-introduction">
      <div class="summary">
        <div class="summary__text">
          <h2 class="headline">
            {{ $t('summary') }}
          </h2>
          <div v-safe-html="session!.description"/>
        </div>
        <div class="summary__media">
          <lazy-image v-if="session!.image" :src="session!.image" :width="384" class="image"/>
        </div>
      </div>

      <div v-if="session!.goals.length" class="goals-section">
        <h2 class="goal-heading">
          {{ $t('session_goals') }}
        </h2>
        <q-scroll-area ref="scrollAreaRef" class="goals-scroll-area" style="max-width: 100%;">
          <div
            class="goals goals-grab"
            @mousedown="onMouseDown"
          >
            <goal-short
              v-for="(goal, i) in session!.goals"
              :key="goal.id"
              :order="i + 1"
              :goal="goal"
              :finished="goal.displayType === LayoutGoalDisplayType.COMPLETE"
            />
          </div>
        </q-scroll-area>
      </div>
    </div>
  </loading-error>
</template>

<style scoped lang="scss">
.session-introduction {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-5);
  padding-bottom: var(--Spacing-spacing-5);
  @include screen-lg {
    padding-bottom: var(--Spacing-spacing-7);
  }
}

.summary {
  display: flex;
  flex-direction: column-reverse;
  gap: var(--Spacing-spacing-5);
  max-width: 900px;

  @include screen-lg {
    flex-direction: row-reverse;
    justify-content: flex-end;
    gap: 0;
  }

  &__text {
    margin: var(--Spacing-spacing-7) var(--Spacing-spacing-8) var(--Spacing-spacing-8) var(--Spacing-spacing-6);

    @include screen-lg {
      @include column-width(6, 10);

    }
  }

  &__media {
    height: auto;
    border-radius: var(--card-border-radius);
    overflow: hidden;
    max-height: 200px;
    object-fit: contain;
    object-position: center;

    @include screen-lg {
      @include column-width(3, 10);
      padding-right: var(--Spacing-spacing-3);
    }
  }
}

.headline {
  @include font-h3-bold;
  margin-bottom: var(--Spacing-spacing-4);
}

.image {
  border-radius: var(--card-border-radius);
  overflow: hidden;
  max-height: 200px;
  object-fit: contain;

  :deep(.q-img) {
    object-fit: contain;
  }

  @include screen-lg {
    height: 100%;
  }
}

.goals-scroll-area {
  // card min-height + padding + scrollbar margin
  height: calc(300px + var(--Spacing-spacing-5) + var(--Spacing-spacing-1));

  @include screen-md {
    height: calc(400px + var(--Spacing-spacing-5));
  }
}

.goals {
  display: flex;
  flex-direction: row;
  gap: var(--Spacing-spacing-3);
  align-items: stretch;
  padding: 6px 0;

  :deep(.goal) {
    flex-shrink: 0;
  }
}

.goals-grab {
  cursor: grab;
  user-select: none;
}

.goal-heading {
  @include font-h3-bold;
  color: var(--Neutral-UI-neutral-ui-100);
  margin-bottom: var(--Spacing-spacing-3);
}
</style>
